import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/client';
import { useAuth } from '../AdminRoutes';
import { useApi, formatDate, timeAgo, humanise } from '../hooks/useApi';
import {
  Card, StatusPill, EmptyState, ErrorState, TableSkeleton,
} from '../components/ui';

const ROLE_NOTE = {
  superadmin: 'Full access, including deleting records and managing this list.',
  admin: 'Can read everything and update records, but cannot delete or manage accounts.',
  viewer: 'Read-only. Can see every screen but change nothing.',
};

export default function Settings() {
  const { user, isSuperadmin } = useAuth();

  return (
    <div className="spa-stack">
      <ChangePassword />
      {isSuperadmin ? <Team currentUserId={user.id} /> : <TeamLocked />}
      <ActivityLog />
    </div>
  );
}

/* ===========================================================================
   Change password
   =========================================================================== */
function ChangePassword() {
  const [values, setValues] = useState({ current_password: '', new_password: '', confirm: '' });
  const [errors, setErrors] = useState({});
  const [isBusy, setIsBusy] = useState(false);

  const set = (name) => (e) => {
    setValues((v) => ({ ...v, [name]: e.target.value }));
    setErrors((p) => (p[name] ? { ...p, [name]: null } : p));
  };

  // Mirrors the server rule in schemas.js, so the failure is immediate.
  const check = () => {
    const e = {};
    if (!values.current_password) e.current_password = 'Enter your current password';
    if (values.new_password.length < 10) e.new_password = 'Use at least 10 characters';
    else if (!/[a-z]/.test(values.new_password)) e.new_password = 'Include a lowercase letter';
    else if (!/[A-Z]/.test(values.new_password)) e.new_password = 'Include an uppercase letter';
    else if (!/\d/.test(values.new_password)) e.new_password = 'Include a number';
    if (values.new_password !== values.confirm) e.confirm = 'These two do not match';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isBusy) return;

    const found = check();
    if (Object.keys(found).length) { setErrors(found); return; }

    setIsBusy(true);
    try {
      const { data } = await api.post('/api/admin/auth/change-password', {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      toast.success(data.message);
      setValues({ current_password: '', new_password: '', confirm: '' });
      setErrors({});
    } catch (err) {
      setErrors(err.fields || {});
      toast.error(err.message);
    } finally {
      setIsBusy(false);
    }
  };

  const Field = ({ name, label, hint }) => (
    <div className="spa-login__field">
      <label className="spa-login__label" htmlFor={name}>{label}</label>
      <input
        id={name} className="spa-input" type="password" autoComplete="new-password"
        value={values[name]} onChange={set(name)}
        aria-invalid={Boolean(errors[name]) || undefined}
      />
      {errors[name]
        ? <p className="form-field-error" role="alert">{errors[name]}</p>
        : hint && <p className="spa-card__note" style={{ marginTop: 5 }}>{hint}</p>}
    </div>
  );

  return (
    <Card title="Your password" note="Change the password you use to sign in to this console.">
      <form onSubmit={submit} noValidate style={{ maxWidth: 380 }}>
        <Field name="current_password" label="Current password" />
        <Field name="new_password" label="New password"
          hint="At least 10 characters, with an uppercase letter, a lowercase letter and a number." />
        <Field name="confirm" label="Confirm new password" />
        <button type="submit" className="spa-btn spa-btn--primary" disabled={isBusy}>
          {isBusy ? 'Saving…' : 'Change password'}
        </button>
      </form>
    </Card>
  );
}

/* ===========================================================================
   Team
   =========================================================================== */
function TeamLocked() {
  return (
    <Card title="Team">
      <EmptyState
        icon="fa-lock"
        title="Only superadmins manage accounts"
        text="Ask a superadmin to add someone or change an access level."
      />
    </Card>
  );
}

function Team({ currentUserId }) {
  const users = useApi('/api/admin/users');
  const [isAdding, setIsAdding] = useState(false);

  const toggleActive = async (u) => {
    const verb = u.is_active ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${verb} ${u.name}? ${u.is_active
      ? 'They will be signed out and cannot sign back in.'
      : 'They will be able to sign in again.'}`)) return;

    try {
      const { data } = await api.patch(`/api/admin/users/${u.id}/active`, {
        is_active: !u.is_active,
      });
      toast.success(data.message);
      users.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Card
      title="Team"
      note="Who can sign in to this console, and what they are allowed to do."
      padded={false}
      actions={
        <button type="button" className="spa-btn spa-btn--sm spa-btn--primary"
          onClick={() => setIsAdding((v) => !v)}>
          <i className={`fa-solid ${isAdding ? 'fa-xmark' : 'fa-plus'}`} aria-hidden="true" />
          {isAdding ? 'Cancel' : 'Add person'}
        </button>
      }
    >
      {isAdding && (
        <div style={{ padding: 18, borderBottom: '1px solid var(--spa-rule)', background: '#fafbfc' }}>
          <AddUser onDone={() => { setIsAdding(false); users.reload(); }} />
        </div>
      )}

      {users.error ? (
        <ErrorState message={users.error} onRetry={users.reload} />
      ) : users.isLoading && !users.data ? (
        <TableSkeleton cols={5} />
      ) : (
        <div className="spa-tablewrap">
          <table className="spa-table">
            <thead>
              <tr>
                <th>Name</th><th>Role</th><th>Access</th>
                <th className="num">Last signed in</th><th />
              </tr>
            </thead>
            <tbody>
              {(users.data || []).map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="spa-table__primary">
                      {u.name}
                      {u.id === currentUserId && (
                        <span className="spa-pill spa-pill--blue" style={{ marginLeft: 8 }}>You</span>
                      )}
                    </div>
                    <div className="spa-table__sub">{u.email}</div>
                  </td>
                  <td>
                    <div>{humanise(u.role)}</div>
                    <div className="spa-table__sub" style={{ maxWidth: 300 }}>{ROLE_NOTE[u.role]}</div>
                  </td>
                  <td><StatusPill status={u.is_active ? 'active' : 'closed'} /></td>
                  <td className="num" title={formatDate(u.last_login_at, { withTime: true })}>
                    {u.last_login_at ? timeAgo(u.last_login_at) : 'Never'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {u.id === currentUserId ? (
                      <span className="spa-card__note">—</span>
                    ) : (
                      <button
                        type="button"
                        className={`spa-btn spa-btn--sm${u.is_active ? ' spa-btn--danger' : ''}`}
                        onClick={() => toggleActive(u)}
                      >
                        {u.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AddUser({ onDone }) {
  const [values, setValues] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [errors, setErrors] = useState({});
  const [isBusy, setIsBusy] = useState(false);

  const set = (name) => (e) => {
    setValues((v) => ({ ...v, [name]: e.target.value }));
    setErrors((p) => (p[name] ? { ...p, [name]: null } : p));
  };

  /** Suggests a password that already satisfies the server's rules. */
  const suggest = () => {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    const pw = `Sp${Array.from(bytes, (b) =>
      'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789'[b % 54]).join('')}7`;
    setValues((v) => ({ ...v, password: pw }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isBusy) return;

    const found = {};
    if (values.name.trim().length < 2) found.name = 'Enter a name';
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(values.email)) found.email = 'Enter a valid email address';
    if (values.password.length < 10) found.password = 'Use at least 10 characters';
    if (Object.keys(found).length) { setErrors(found); return; }

    setIsBusy(true);
    try {
      const { data } = await api.post('/api/admin/users', values);
      toast.success(data.message);
      onDone();
    } catch (err) {
      setErrors(err.fields || {});
      toast.error(err.message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className="spa-grid spa-grid--3" style={{ gap: 12 }}>
        <div>
          <label className="spa-login__label" htmlFor="nu-name">Name</label>
          <input id="nu-name" className="spa-input" value={values.name} onChange={set('name')}
            aria-invalid={Boolean(errors.name) || undefined} />
          {errors.name && <p className="form-field-error">{errors.name}</p>}
        </div>
        <div>
          <label className="spa-login__label" htmlFor="nu-email">Email</label>
          <input id="nu-email" className="spa-input" type="email" value={values.email}
            onChange={set('email')} aria-invalid={Boolean(errors.email) || undefined} />
          {errors.email && <p className="form-field-error">{errors.email}</p>}
        </div>
        <div>
          <label className="spa-login__label" htmlFor="nu-role">Role</label>
          <select id="nu-role" className="spa-select" style={{ width: '100%' }}
            value={values.role} onChange={set('role')}>
            <option value="viewer">Viewer — read only</option>
            <option value="admin">Admin — can edit records</option>
            <option value="superadmin">Superadmin — full access</option>
          </select>
        </div>
      </div>

      <div style={{ marginTop: 12, maxWidth: 420 }}>
        <label className="spa-login__label" htmlFor="nu-password">Temporary password</label>
        <div className="spa-row">
          <input id="nu-password" className="spa-input" value={values.password}
            onChange={set('password')} aria-invalid={Boolean(errors.password) || undefined} />
          <button type="button" className="spa-btn spa-btn--sm" onClick={suggest}>Generate</button>
        </div>
        {errors.password
          ? <p className="form-field-error">{errors.password}</p>
          : <p className="spa-card__note" style={{ marginTop: 5 }}>
              Share this with them directly. Ask them to change it after their first sign-in.
            </p>}
      </div>

      <button type="submit" className="spa-btn spa-btn--primary" style={{ marginTop: 14 }}
        disabled={isBusy}>
        {isBusy ? 'Creating…' : 'Create account'}
      </button>
    </form>
  );
}

/* ===========================================================================
   Activity log
   =========================================================================== */
function ActivityLog() {
  const [limit, setLimit] = useState(50);
  const activity = useApi('/api/admin/activity', { limit });

  return (
    <Card
      title="Activity log"
      note="Every change made from this console, newest first."
      padded={false}
      actions={
        <button type="button" className="spa-btn spa-btn--sm"
          onClick={() => setLimit((n) => Math.min(n + 50, 200))}
          disabled={limit >= 200}>
          {limit >= 200 ? 'Showing 200' : 'Show more'}
        </button>
      }
    >
      {activity.isLoading && !activity.data ? (
        <TableSkeleton cols={4} />
      ) : !activity.data?.length ? (
        <EmptyState
          icon="fa-clock-rotate-left"
          title="Nothing recorded yet"
          text="Status changes, notes, exports and sign-ins are logged here as they happen."
        />
      ) : (
        <div className="spa-tablewrap">
          <table className="spa-table">
            <thead>
              <tr><th>Who</th><th>Did what</th><th>To</th><th className="num">When</th></tr>
            </thead>
            <tbody>
              {activity.data.map((a) => (
                <tr key={a.id}>
                  <td className="spa-table__primary">{a.admin_email || 'System'}</td>
                  <td>{humanise(a.action.replace(/\./g, ' '))}</td>
                  <td className="spa-table__sub">
                    {a.entity_type
                      ? `${a.entity_type} ${String(a.entity_id || '').slice(0, 8)}`
                      : '—'}
                  </td>
                  <td className="num" title={formatDate(a.created_at, { withTime: true })}>
                    {timeAgo(a.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
